const SELECTORS = {
    menu: '.offcanvas__menu',
    menuItem: '.menu-item-has-children',
    link: ':scope > a',
    panel: '.menu-submenu',
    trigger: '.accordion-trigger',
};

const CLASSES = {
    trigger: 'accordion-trigger',
    icon: 'accordion-icon',
    srOnly: 'sr-only',
    isOpen: 'is-open',
    panelHidden: 'is-hidden',
};

const ATTRS = {
    expanded: 'aria-expanded',
    controls: 'aria-controls',
    labelledby: 'aria-labelledby',
    hidden: 'hidden',
    role: 'role',
};

export const OffcanvasNavigation = () => {
    const menus = document.querySelectorAll(SELECTORS.menu);

    menus.forEach((menu) => {
        new Accordion(menu, {
            allowMultiple: false,
        });
    });
};

class Accordion {
    constructor(domNode, options = {}) {
        this.rootNode = domNode;
        this.allowMultiple = options.allowMultiple ?? false;
        this.menuItems = [...this.rootNode.querySelectorAll(SELECTORS.menuItem)];
        this.triggers = [];
        this.panels = [];
        this.links = [];

        this.menuItems.forEach((item, index) => {
            this.initItem(item, index);
        });

        this.rootNode.addEventListener('keydown', this.onRootKeyDown.bind(this));
    }

    initItem(item, index) {
        const link = item.querySelector(SELECTORS.link);
        const panel = item.querySelector(SELECTORS.panel);

        if (!link || !panel) return;

        const id = uid();
        const triggerId = `accordion-trigger-${id}`;
        const panelId = `accordion-panel-${id}`;

        // Make link non-clickable and act as trigger
        link.setAttribute('role', 'button');
        link.setAttribute(ATTRS.expanded, 'false');
        link.setAttribute(ATTRS.controls, panelId);
        link.removeAttribute('href');

        // Create toggle button
        const button = document.createElement('button');
        button.setAttribute('type', 'button');
        button.setAttribute('id', triggerId);
        button.setAttribute('aria-label', `Toggle ${link.textContent.trim()} submenu`);
        button.setAttribute('tabindex', '-1');
        button.classList.add(CLASSES.trigger);

        button.innerHTML = `
            <svg
                aria-hidden="true"
                class="${CLASSES.icon}"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 30.727 30.727"
            >
                <path
                    fill="currentColor"
                    d="M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0 l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z"
                />
            </svg>
        `;

        // Setup panel - use class instead of hidden attribute for animation
        panel.setAttribute('id', panelId);
        panel.setAttribute(ATTRS.role, 'region');
        panel.setAttribute(ATTRS.labelledby, triggerId);
        panel.classList.add(CLASSES.panelHidden);

        // Insert button after link
        link.insertAdjacentElement('afterend', button);

        // Store references
        this.triggers.push(button);
        this.panels.push(panel);
        this.links.push(link);

        // Event listeners - both link and button toggle
        link.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggle(index);
        });
        button.addEventListener('click', () => this.toggle(index));
    }

    toggle(index) {
        const isExpanded = this.links[index].getAttribute(ATTRS.expanded) === 'true';

        if (!this.allowMultiple && !isExpanded) {
            this.closeAll();
        }

        if (isExpanded) {
            this.close(index);
        } else {
            this.open(index);
        }
    }

    open(index) {
        this.links[index].setAttribute(ATTRS.expanded, 'true');
        this.panels[index].classList.remove(CLASSES.panelHidden);
        this.menuItems[index].classList.add(CLASSES.isOpen);
    }

    close(index) {
        this.links[index].setAttribute(ATTRS.expanded, 'false');
        this.panels[index].classList.add(CLASSES.panelHidden);
        this.menuItems[index].classList.remove(CLASSES.isOpen);
    }

    closeAll() {
        this.triggers.forEach((_, index) => this.close(index));
    }

    onRootKeyDown(event) {
        const target = event.target;

        // Check if focus is on a link with submenu
        const index = this.links.indexOf(target);
        if (index === -1) return;

        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.toggle(index);
                break;

            case 'ArrowDown':
                event.preventDefault();
                this.focusNextLink(index);
                break;

            case 'ArrowUp':
                event.preventDefault();
                this.focusPrevLink(index);
                break;

            case 'Home':
                event.preventDefault();
                this.links[0]?.focus();
                break;

            case 'End':
                event.preventDefault();
                this.links[this.links.length - 1]?.focus();
                break;

            case 'Escape':
                if (this.links[index].getAttribute(ATTRS.expanded) === 'true') {
                    this.close(index);
                }
                break;
        }
    }

    focusNextLink(currentIndex) {
        const nextIndex = (currentIndex + 1) % this.links.length;
        this.links[nextIndex]?.focus();
    }

    focusPrevLink(currentIndex) {
        const prevIndex = (currentIndex - 1 + this.links.length) % this.links.length;
        this.links[prevIndex]?.focus();
    }
}

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
